import React, { useEffect, useState, useRef } from 'react';
import { getData, postData, deleteData, putData } from '../../services/apiService';
import Layout from '../../components/layout/Layout';
import 'bootstrap/dist/css/bootstrap.min.css';
import Modal from 'react-bootstrap/Modal';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    IconButton, Button, TextField, Select, MenuItem, Pagination, InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Progress from '../../hooks/useProgressBar';
import useDebounce from '../../hooks/useDebounce';

const AddSection = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [courseData, setCourseData] = useState([]);
    const isFetched = useRef(false);
    const [showSectionModal, addSectionShowModal] = useState(false);
    const [updateSectionModalShow, updateSectionModal] = useState(false);
    const [formData, setFormData] = useState({ course: "", title: "", order: "" });
    const [selectedSection, setSelectedSection] = useState(null);

    // Pagination and search states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [totalPages, setTotalPages] = useState(1);

    // onload
    useEffect(() => {
        if (!isFetched.current) {
            fetchData();
            getCourse();
        }
    }, [page, rowsPerPage, debouncedSearchQuery]);

    // Fetch all Sections from the API
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData(`/get-all-sections?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}`);
            if (response) {
                setData(response.data);
                setTotalPages(response.totalPages);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch data', error);
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1); // Reset to first page on new search
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    // Fetch all Courses from the API
    const getCourse = async () => {
        try {
            const response = await getData('/get-courses-for-dropdown');
            if (response) {
                setCourseData(response);
            }
        } catch (error) {
            console.error('Failed to fetch data', error);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Add new Section!
    const addSection = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postData('/create-section', formData);
            setFormData({ course: "", title: "", order: "" });
            addSectionShowModal(false);
            fetchData();
            setLoading(false);
        } catch (error) {
            console.error('Failed to post data', error);
            setLoading(false);
        }
    };

    // Edit Section
    const editSection = (id) => {
        const sectionData = data.find(section => section._id === id);
        setSelectedSection(sectionData);
        updateSectionModal(true);
    };

    // Update Section
    const updateSection = async () => {
        if (!selectedSection) return;
        const updatedData = {
            title: selectedSection.title,
            order: selectedSection.order,
            course: selectedSection?.course?._id || selectedSection.course,
        };

        try {
            await putData(`/update-section/${selectedSection._id}`, updatedData);
            fetchData();
            updateSectionModal(false);
        } catch (error) {
            console.error('Failed to update user', error);
        }

    };

    // Delete Section!
    const deleteSection = async (id) => {
        try {
            await deleteData(`/delete-section/${id}`);
            await fetchData();
        } catch (error) {
            console.error("Error:", error);
        }
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>Section Data</Typography>
                    {/* Header: Add Button (left) and Search (right) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                        <Button variant="contained" sx={{
                            borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                            '&:hover': { backgroundColor: '#3A3897', },
                        }} onClick={() => addSectionShowModal(true)} > + Add Section </Button>
                        <TextField placeholder="Search Departments" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                            sx={{ width: 350, bgcolor: '#e9ecef', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, }, }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                            }}
                        />
                    </Box>
                    {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}

                    <TableContainer component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Title </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Course Name </TableCell>
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Order </TableCell> */}
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}> Action </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={item._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.title}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.course.courseName}</Typography>
                                        </TableCell>
                                        {/* <TableCell align="center">
                                            <Typography variant="body1">{item.order}</Typography>
                                        </TableCell> */}
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editSection(item._id)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteSection(item._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>

                    {/* Section Add model */}
                    <Modal show={showSectionModal} onHide={() => addSectionShowModal(false)} style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Add Section</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <form onSubmit={addSection}>
                                <div className="mb-3">
                                    <label htmlFor="title" className="col-form-label">Title:</label>
                                    <input type="text" className="form-control" id="title" placeholder="Title" name='title' value={formData.title} onChange={handleInputChange} />
                                    <label className="col-form-label">Course:</label>
                                    <select className="form-select" name="course" value={formData.course} onChange={handleInputChange} >
                                        <option value="">Select Course</option>
                                        {courseData.map((course) => (
                                            <option key={course._id} value={course._id}>{course.courseName}</option>
                                        ))}
                                    </select>
                                    {/* <label htmlFor="order" className="col-form-label">Order:</label>
                                    <input type="number" className="form-control" id="order" placeholder="Order" name='order' value={formData.order} onChange={handleInputChange} /> */}
                                </div>
                                <Modal.Footer>
                                    <Button variant="contained" type="submit" color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>Save</Button>
                                </Modal.Footer>
                            </form>
                        </Modal.Body>
                    </Modal>

                    {/* Section update model */}
                    <Modal show={updateSectionModalShow} onHide={() => updateSectionModal(false)} style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Update Section</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <form>
                                <div className="mb-3">
                                    <label htmlFor="title" className="col-form-label">Title:</label>
                                    <input type="text" className="form-control" value={selectedSection?.title || ""} onChange={(e) => setSelectedSection({ ...selectedSection, title: e.target.value })} required />

                                    <label className="col-form-label">Course:</label>
                                    <select className="form-select" name="course" value={selectedSection?.course?._id} onChange={(e) => setSelectedSection({ ...selectedSection, course: e.target.value })} >
                                        <option value="">Select Course</option>
                                        {courseData.map((course) => (
                                            <option key={course._id} value={course._id}>{course.courseName}</option>
                                        ))}
                                    </select>

                                    {/* <label htmlFor="order" className="col-form-label">Order:</label>
                                    <input type="number" className="form-control" value={selectedSection?.order || ""} onChange={(e) => setSelectedSection({ ...selectedSection, order: e.target.value })} required /> */}
                                </div>
                                <Modal.Footer>
                                    <Button variant="contained" onClick={updateSection} color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>Save changes</Button>
                                </Modal.Footer>
                            </form>
                        </Modal.Body>
                    </Modal>
                </Box>
            }
        </Layout>
    );
};

export default AddSection;